import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { 
  ArrowLeft, 
  Building2, 
  Upload, 
  Palette, 
  Save,
  User,
  Mail,
  DollarSign,
  Users,
  Image as ImageIcon,
  Check,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Bell,
  Megaphone,
  LayoutDashboard,
  Mic,
  Brain,
  Smile,
  Camera,
  FileText,
  Link2,
  Trophy,
  Flame,
  Award,
  Share2,
  Moon,
  Eye,
  TrendingUp,
  Copy,
  Globe,
  Loader2,
  BookOpen,
  Heart,
  Sparkles,
  Star,
  Target,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Video,
  Key,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast, Toaster } from 'sonner';
import Footer from '@/components/layout/Footer';
import { useDialog } from '@/context/DialogContext';

const API_BASE = API.replace('/api', '');

const EditClientPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [customMoodUploading, setCustomMoodUploading] = useState(false);
  const [customMoodPreview, setCustomMoodPreview] = useState(null);
  const [pdfMoodUploading, setPdfMoodUploading] = useState(false);
  const [pdfMoodPreview, setPdfMoodPreview] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const { confirm } = useDialog();
  
  const DEFAULT_FEATURES = {
    ai_coach: true,
    voice_typing: true,
    mood_tracking: true,
    image_attachments: true,
    pdf_export: true,
    link_preview: true,
    gamification: true,
    streaks: true,
    badges: true,
    sidebar_promo: true,
    main_promo_banner: true,
    push_notifications: true,
    referral_system: true,
    video_embeds: true,
    themes: true,
    dark_mode: true,
    client_admin_portal: true,
    analytics_dashboard: true,
    advanced_stats: true,  // Legacy alias for analytics_dashboard
    show_powered_by: true,
    show_upgrade_prompts: true
  };

  // EXPANDED 8-COLOR THEME SYSTEM
  const DEFAULT_THEME = {
    brand_color: '#3B82F6',
    background_color: '#0F172A',
    card_color: '#1E293B',
    text_color: '#F8FAFC',
    border_color: '#475569',
    accent_color: '#22D3EE',
    secondary_text_color: '#94A3B8',
    input_background: '#1E293B'
  };

  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_phone: '',
    contact_address: '',
    total_licenses: 10,
    price_paid: 0,
    start_date: '',
    end_date: '',
    logo_url: '',
    app_name: 'Journal',
    ai_coach_name: 'Your Journal Coach',
    features: { ...DEFAULT_FEATURES },
    theme: { ...DEFAULT_THEME },
    custom_domain: '',
    company_name: '',
    privacy_policy_url: '',
    terms_url: '',
    support_email: '',
    copyright_text: '',
    // Custom Mood (Premium Feature)
    custom_mood_enabled: false,
    custom_mood_image_url: '',
    custom_mood_label: '',
    custom_mood_pdf_image_url: ''
  });
  
  const [savingSections, setSavingSections] = useState({});

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      
      const res = await axios.get(`${API_BASE}/api/admin/clients/${clientId}`, {
        withCredentials: true
      });
      
      const client = res.data;
      setFormData({
        name: client.name || '',
        contact_email: client.contact_email || '',
        contact_name: client.contact_name || '',
        contact_first_name: client.contact_first_name || '',
        contact_last_name: client.contact_last_name || '',
        contact_phone: client.contact_phone || '',
        contact_address: client.contact_address || '',
        total_licenses: client.total_licenses || 10,
        price_paid: client.price_paid || 0,
        start_date: client.start_date ? client.start_date.split('T')[0] : '',
        end_date: client.end_date ? client.end_date.split('T')[0] : '',
        logo_url: client.logo_url || '',
        app_name: client.app_name || 'Journal',
        ai_coach_name: client.ai_coach_name || 'Your Journal Coach',
        features: { ...DEFAULT_FEATURES, ...client.features },
        // Map legacy theme field names to new expanded 8-color system
        theme: {
          ...DEFAULT_THEME,
          // Core 4 colors
          brand_color: client.theme?.brand_color || client.theme?.primary_color || DEFAULT_THEME.brand_color,
          background_color: client.theme?.background_color || client.theme?.background_primary || DEFAULT_THEME.background_color,
          card_color: client.theme?.card_color || client.theme?.background_secondary || DEFAULT_THEME.card_color,
          text_color: client.theme?.text_color || client.theme?.text_primary || DEFAULT_THEME.text_color,
          // Additional 4 colors for depth
          border_color: client.theme?.border_color || client.theme?.card_color || client.theme?.background_secondary || DEFAULT_THEME.border_color,
          accent_color: client.theme?.accent_color || client.theme?.brand_color || client.theme?.primary_color || DEFAULT_THEME.accent_color,
          secondary_text_color: client.theme?.secondary_text_color || client.theme?.text_secondary || DEFAULT_THEME.secondary_text_color,
          input_background: client.theme?.input_background || client.theme?.card_color || client.theme?.background_secondary || DEFAULT_THEME.input_background
        },
        custom_domain: client.custom_domain || '',
        company_name: client.company_name || '',
        privacy_policy_url: client.privacy_policy_url || '',
        terms_url: client.terms_url || '',
        support_email: client.support_email || '',
        copyright_text: client.copyright_text || '',
        // Custom Mood (Premium Feature)
        custom_mood_enabled: client.custom_mood_enabled || false,
        custom_mood_image_url: client.custom_mood_image_url || '',
        custom_mood_label: client.custom_mood_label || '',
        custom_mood_pdf_image_url: client.custom_mood_pdf_image_url || ''
      });
      
      if (client.logo_url) {
        setLogoPreview(client.logo_url);
      }
      
      if (client.custom_mood_image_url) {
        setCustomMoodPreview(client.custom_mood_image_url);
      }
      
      if (client.custom_mood_pdf_image_url) {
        setPdfMoodPreview(client.custom_mood_pdf_image_url);
      }
    } catch (error) {
      console.error('Failed to load client data:', error);
      toast.error('Failed to load client data');
      // Don't navigate away - let user stay on page and retry
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async () => {
    const confirmed = await confirm({
      title: 'Reset Client Password',
      message: `This will generate a new password for "${formData.name}". The client will need to use the new password to log into their portal. Continue?`,
      confirmText: 'Reset Password',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    
    if (!confirmed) return;
    
    setResettingPassword(true);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/clients/${clientId}/reset-password`, {}, {
        withCredentials: true
      });
      
      setNewCredentials({
        admin_username: res.data.admin_username,
        new_password: res.data.new_password
      });
      setShowPasswordModal(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };
  
  const copyCredentials = () => {
    if (!newCredentials) return;
    const text = `Client Portal Credentials for ${formData.name}\n\nPortal URL: ${window.location.origin}/client-portal\nClient ID: ${newCredentials.admin_username}\nPassword: ${newCredentials.new_password}`;
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard!');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Use the general image upload endpoint with credentials
      const res = await axios.post(`${API_BASE}/api/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setFormData(prev => ({ ...prev, logo_url: res.data.url }));
      setLogoPreview(res.data.url);
      toast.success('Logo uploaded');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleCustomMoodUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type (SVG or PNG)
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an SVG, PNG, JPG, or WebP image');
      return;
    }
    
    // Validate file size (max 1MB for mood icon)
    if (file.size > 1024 * 1024) {
      toast.error('Custom mood image must be under 1MB');
      return;
    }
    
    setCustomMoodUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setFormData(prev => ({ ...prev, custom_mood_image_url: res.data.url }));
      setCustomMoodPreview(res.data.url);
      toast.success('Custom mood image uploaded');
    } catch (error) {
      console.error('Custom mood upload error:', error);
      toast.error('Failed to upload custom mood image');
    } finally {
      setCustomMoodUploading(false);
    }
  };

  // Handle PDF mood image upload (server-hosted for PDF generation)
  const handlePdfMoodUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an SVG, PNG, JPG, or WebP image');
      return;
    }
    
    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast.error('PDF mood image must be under 1MB');
      return;
    }
    
    setPdfMoodUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setFormData(prev => ({ ...prev, custom_mood_pdf_image_url: res.data.url }));
      setPdfMoodPreview(res.data.url);
      toast.success('PDF mood image uploaded');
    } catch (error) {
      console.error('PDF mood upload error:', error);
      toast.error('Failed to upload PDF mood image');
    } finally {
      setPdfMoodUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.contact_email) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      
      await axios.put(`${API_BASE}/api/admin/clients/${clientId}`, formData, {
        withCredentials: true
      });
      
      toast.success('All changes saved successfully!');
      // Stay on the page - don't navigate away
      // User can use the back button if they want to leave
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  // Save a specific section without navigating away
  const saveSection = async (sectionName) => {
    if (!formData.name || !formData.contact_email) {
      toast.error('Company Name and Contact Email are required');
      return;
    }

    setSavingSections(prev => ({ ...prev, [sectionName]: true }));
    try {
      await axios.put(`${API_BASE}/api/admin/clients/${clientId}`, formData, {
        withCredentials: true
      });
      toast.success(`${sectionName} saved!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to save ${sectionName}`);
    } finally {
      setSavingSections(prev => ({ ...prev, [sectionName]: false }));
    }
  };

  // Delete client function
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/api/admin/clients/${clientId}`, {
        withCredentials: true
      });
      toast.success('Client deleted successfully');
      navigate('/admin?tab=b2b');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Section Save Button component
  const SectionSaveButton = ({ sectionName }) => (
    <div className="mt-4 pt-4 border-t border-[#4A0A66]/50 flex justify-end">
      <Button
        data-testid={`edit-client-save-${sectionName.toLowerCase().replaceAll(' ', '-')}`}
        type="button"
        onClick={() => saveSection(sectionName)}
        disabled={savingSections[sectionName]}
        className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
        size="sm"
      >
        {savingSections[sectionName] ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            Save {sectionName}
          </>
        )}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading client data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0527] via-[#2D063E] to-[#1a0527]">
      {/* Toast notifications */}
      <Toaster richColors position="top-center" />
      
      {/* Header */}
      <div className="border-b border-[#4A0A66]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-[#4A0A66]/30 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#FDF2F8]/70" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-[#FF7901]" />
                  Edit B2B Client
                </h1>
                <p className="text-sm text-[#FDF2F8]/50">{formData.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Client
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#0FFFE2]" />
              Client Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Company Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Corp"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Contact Email *</label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@company.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Contact First Name</label>
                <Input
                  value={formData.contact_first_name}
                  onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                  placeholder="John"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Contact Last Name</label>
                <Input
                  value={formData.contact_last_name}
                  onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                  placeholder="Smith"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Contact Phone</label>
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Total Licenses</label>
                <Input
                  type="number"
                  value={formData.total_licenses}
                  onChange={(e) => setFormData({ ...formData, total_licenses: parseInt(e.target.value) || 0 })}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Contact Address</label>
                <Input
                  value={formData.contact_address}
                  onChange={(e) => setFormData({ ...formData, contact_address: e.target.value })}
                  placeholder="123 Main St, Suite 100, City, State 12345"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>
            <SectionSaveButton sectionName="Client Info" />
          </div>

          {/* Portal Access */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-[#FF7901]" />
              Portal Access
            </h2>
            <div className="flex items-center justify-between p-4 bg-[#4A0A66]/20 rounded-lg border border-[#4A0A66]/50">
              <div>
                <p className="text-white font-medium">Client Portal Credentials</p>
                <p className="text-sm text-[#FDF2F8]/60">
                  Reset the password if the client needs new login credentials
                </p>
              </div>
              <Button
                type="button"
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="bg-gradient-to-r from-[#FF7901] to-[#df1283] hover:opacity-90"
              >
                {resettingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* License Dates */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#22c55e]" />
              License Terms
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Amount Paid</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price_paid}
                  onChange={(e) => setFormData({ ...formData, price_paid: parseFloat(e.target.value) || 0 })}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Start Date</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">End Date</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white [color-scheme:dark]"
                />
              </div>
            </div>
            <SectionSaveButton sectionName="License Terms" />
          </div>

          {/* Client Logo */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#df1283]" />
              Client Logo
            </h2>
            <div className="flex items-start gap-6">
              <div className="min-w-24 max-w-48 min-h-24 max-h-48 rounded-xl border-2 border-dashed border-[#4A0A66] flex items-center justify-center bg-[#4A0A66]/20 overflow-hidden p-2">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-[#FDF2F8]/30" />
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[#4A0A66] text-[#FDF2F8]/70 cursor-pointer"
                    disabled={logoUploading}
                    asChild
                  >
                    <span>
                      {logoUploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {logoUploading ? 'Uploading...' : (logoPreview ? 'Change Logo' : 'Upload Logo')}
                    </span>
                  </Button>
                </label>
                {logoPreview && (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 w-full"
                    onClick={() => {
                      setLogoPreview(null);
                      setFormData(prev => ({ ...prev, logo_url: '' }));
                      toast.success('Logo removed - click Save to confirm');
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Logo
                  </Button>
                )}
                <p className="text-xs text-[#FDF2F8]/40 mt-2">PNG, JPG up to 2MB. Any aspect ratio supported.</p>
              </div>
            </div>
            <SectionSaveButton sectionName="Logo" />
          </div>

          {/* Custom Mood - Premium Feature */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#FF7901]/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FF7901]" />
                  Custom Branded Mood
                  <span className="text-xs bg-[#FF7901]/20 text-[#FF7901] px-2 py-0.5 rounded-full">Premium</span>
                </h2>
                <p className="text-sm text-[#FDF2F8]/60 mt-1">
                  Replace the default "UnCloned®" mood with this client's own branded mood option
                </p>
              </div>
              <div 
                data-testid="edit-client-custom-mood-toggle"
                role="switch"
                aria-checked={formData.custom_mood_enabled}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                  formData.custom_mood_enabled 
                    ? 'bg-[#22c55e]/20 border border-[#22c55e]/50' 
                    : 'bg-[#4A0A66]/30 border border-[#4A0A66]'
                }`}
                onClick={() => setFormData(prev => ({ ...prev, custom_mood_enabled: !prev.custom_mood_enabled }))}
              >
                <span className="text-sm text-white">{formData.custom_mood_enabled ? 'Enabled' : 'Disabled'}</span>
                {formData.custom_mood_enabled ? (
                  <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                )}
              </div>
            </div>

            {formData.custom_mood_enabled ? (
              <div className="space-y-4">
                {/* Preview */}
                <div className="bg-[#1a0527] rounded-xl p-4 border border-[#4A0A66]">
                  <p className="text-xs text-[#FDF2F8]/50 mb-3">Preview (colors will inherit client's theme)</p>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-20 h-20 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: formData.theme?.card_color || '#1E293B' }}
                    >
                      {customMoodPreview ? (
                        <img 
                          src={customMoodPreview} 
                          alt="Custom mood" 
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-[#FDF2F8]/30" />
                      )}
                    </div>
                    <div>
                      <p 
                        className="text-lg font-medium"
                        style={{ color: formData.theme?.text_color || '#F8FAFC' }}
                      >
                        {formData.custom_mood_label || 'Custom Mood'}
                      </p>
                      <p className="text-xs text-[#FDF2F8]/50">This will appear in the mood picker</p>
                    </div>
                  </div>
                </div>

                {/* Upload and Label */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                      Mood Icon (SVG or PNG)
                    </label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-16 h-16 rounded-lg border-2 border-dashed border-[#4A0A66] flex items-center justify-center bg-[#4A0A66]/20 overflow-hidden"
                      >
                        {customMoodPreview ? (
                          <img src={customMoodPreview} alt="Mood icon" className="w-10 h-10 object-contain" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-[#FDF2F8]/30" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <input
                          type="file"
                          accept="image/svg+xml,image/png,image/jpeg,image/webp"
                          onChange={handleCustomMoodUpload}
                          className="hidden"
                          id="custom-mood-upload"
                        />
                        <label htmlFor="custom-mood-upload">
                          <Button
                            type="button"
                            variant="outline"
                            className="border-[#FF7901] text-[#FF7901] cursor-pointer"
                            disabled={customMoodUploading}
                            asChild
                          >
                            <span>
                              {customMoodUploading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              {customMoodUploading ? 'Uploading...' : (customMoodPreview ? 'Change' : 'Upload')}
                            </span>
                          </Button>
                        </label>
                        {customMoodPreview && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 text-xs"
                            onClick={() => {
                              setCustomMoodPreview(null);
                              setFormData(prev => ({ ...prev, custom_mood_image_url: '' }));
                            }}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#FDF2F8]/40 mt-2">SVG recommended. Max 1MB.</p>
                  </div>

                  {/* Label Input */}
                  <div>
                    <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                      Mood Label
                    </label>
                    <Input
                      data-testid="edit-client-custom-mood-label-input"
                      value={formData.custom_mood_label}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_mood_label: e.target.value }))}
                      placeholder="e.g., Oh Yeah!, Inspired, Branded"
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                      maxLength={20}
                    />
                    <p className="text-xs text-[#FDF2F8]/40 mt-2">
                      {formData.custom_mood_label.length}/20 characters
                    </p>
                  </div>
                </div>

                {/* PDF Mood Image Upload */}
                <div className="mt-4 pt-4 border-t border-[#4A0A66]">
                  <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                    PDF Mood Image (Optional)
                  </label>
                  <p className="text-xs text-[#FDF2F8]/40 mb-3">
                    Upload a server-hosted version of your mood icon for PDF exports. This ensures the mood icon displays correctly in exported PDFs.
                  </p>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-[#0FFFE2]/50 flex items-center justify-center bg-[#0FFFE2]/10 overflow-hidden"
                    >
                      {pdfMoodPreview ? (
                        <img src={pdfMoodPreview} alt="PDF Mood icon" className="w-10 h-10 object-contain" />
                      ) : (
                        <FileText className="w-6 h-6 text-[#0FFFE2]/30" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="image/svg+xml,image/png,image/jpeg,image/webp"
                        onChange={handlePdfMoodUpload}
                        className="hidden"
                        id="pdf-mood-upload"
                      />
                      <label htmlFor="pdf-mood-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-[#0FFFE2] text-[#0FFFE2] cursor-pointer"
                          disabled={pdfMoodUploading}
                          asChild
                        >
                          <span>
                            {pdfMoodUploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {pdfMoodUploading ? 'Uploading...' : (pdfMoodPreview ? 'Change' : 'Upload for PDF')}
                          </span>
                        </Button>
                      </label>
                      {pdfMoodPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 text-xs"
                          onClick={() => {
                            setPdfMoodPreview(null);
                            setFormData(prev => ({ ...prev, custom_mood_pdf_image_url: '' }));
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-[#FF7901]/10 border border-[#FF7901]/30 rounded-lg p-3">
                  <p className="text-xs text-[#FF7901]">
                    <strong>Note:</strong> This custom mood will replace the "UnCloned®" option only in this client's B2B portal. 
                    The main app remains unaffected.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-[#1a0527] rounded-xl p-6 border border-[#4A0A66] text-center">
                <div className="w-12 h-12 rounded-full bg-[#4A0A66]/30 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-[#FDF2F8]/30" />
                </div>
                <p className="text-[#FDF2F8]/50 text-sm">
                  Custom branded mood is disabled for this client.
                </p>
                <p className="text-[#FDF2F8]/30 text-xs mt-2">
                  Enable to let this client have their own branded mood option in the mood picker.
                </p>
              </div>
            )}
            <SectionSaveButton sectionName="Custom Mood" />
          </div>

          {/* Feature Toggles */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ToggleLeft className="w-5 h-5 text-[#0FFFE2]" />
              Feature Access
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-6">
              Enable or disable specific features for this client
            </p>
            
            {/* Feature categories */}
            <div className="space-y-6">
              {/* Core Features */}
              <div>
                <h3 className="text-sm font-medium text-[#0FFFE2] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Core Journal Features
                </h3>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    { key: 'ai_coach', label: 'AI Coach', icon: Brain },
                    { key: 'voice_typing', label: 'Voice Typing', icon: Mic },
                    { key: 'mood_tracking', label: 'Mood Tracking', icon: Smile },
                    { key: 'image_attachments', label: 'Image Attachments', icon: Camera },
                    { key: 'pdf_export', label: 'PDF Export', icon: FileText },
                    { key: 'link_preview', label: 'Link Preview', icon: Link2 }
                  ].map(({ key, label, icon: Icon }) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.features[key] 
                          ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                          : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                      }`}
                      onClick={() => setFormData({
                        ...formData,
                        features: { ...formData.features, [key]: !formData.features[key] }
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                          <span className="text-sm text-white">{label}</span>
                        </div>
                        {formData.features[key] ? (
                          <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gamification */}
              <div>
                <h3 className="text-sm font-medium text-[#FF7901] mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Gamification
                </h3>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    { key: 'gamification', label: 'Gamification', icon: Trophy },
                    { key: 'streaks', label: 'Streaks', icon: Flame },
                    { key: 'badges', label: 'Badges', icon: Award }
                  ].map(({ key, label, icon: Icon }) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.features[key] 
                          ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                          : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                      }`}
                      onClick={() => setFormData({
                        ...formData,
                        features: { ...formData.features, [key]: !formData.features[key] }
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                          <span className="text-sm text-white">{label}</span>
                        </div>
                        {formData.features[key] ? (
                          <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Promotional & Marketing */}
              <div>
                <h3 className="text-sm font-medium text-[#df1283] mb-3 flex items-center gap-2">
                  <Megaphone className="w-4 h-4" />
                  Promotional & Marketing
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: 'sidebar_promo', label: 'Sidebar Promo Videos', icon: Megaphone, desc: 'Rotating motivational videos' },
                    { key: 'main_promo_banner', label: 'Promo Banner Carousel', icon: ImageIcon, desc: 'Dashboard promotional banners' },
                    { key: 'push_notifications', label: 'Push Notifications', icon: Bell, desc: 'In-app notification system' },
                    { key: 'referral_system', label: 'Referral System', icon: Share2, desc: 'User referral program' },
                    { key: 'video_embeds', label: 'Video Embeds', icon: Video, desc: 'Custom embedded videos for users' }
                  ].map(({ key, label, icon: Icon, desc }) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.features[key] 
                          ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                          : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                      }`}
                      onClick={() => setFormData({
                        ...formData,
                        features: { ...formData.features, [key]: !formData.features[key] }
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                          <div>
                            <span className="text-sm text-white">{label}</span>
                            <p className="text-xs text-[#FDF2F8]/40">{desc}</p>
                          </div>
                        </div>
                        {formData.features[key] ? (
                          <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Appearance */}
              <div>
                <h3 className="text-sm font-medium text-[#8B5CF6] mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Appearance
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: 'themes', label: 'Theme Customization', icon: Palette, desc: 'User-customizable themes' },
                    { key: 'dark_mode', label: 'Dark/Light Mode', icon: Moon, desc: 'Toggle between modes' }
                  ].map(({ key, label, icon: Icon, desc }) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.features[key] 
                          ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                          : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                      }`}
                      onClick={() => setFormData({
                        ...formData,
                        features: { ...formData.features, [key]: !formData.features[key] }
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                          <div>
                            <span className="text-sm text-white">{label}</span>
                            <p className="text-xs text-[#FDF2F8]/40">{desc}</p>
                          </div>
                        </div>
                        {formData.features[key] ? (
                          <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Features */}
              <div>
                <h3 className="text-sm font-medium text-[#22C55E] mb-3 flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />
                  Admin Features
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: 'client_admin_portal', label: 'Client Admin Portal', icon: LayoutDashboard, desc: 'Self-serve management dashboard' },
                    { key: 'advanced_stats', label: 'Analytics Dashboard', icon: TrendingUp, desc: 'User analytics & insights' }
                  ].map(({ key, label, icon: Icon, desc }) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.features[key] 
                          ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                          : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                      }`}
                      onClick={() => setFormData({
                        ...formData,
                        features: { ...formData.features, [key]: !formData.features[key] }
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                          <div>
                            <span className="text-sm text-white">{label}</span>
                            <p className="text-xs text-[#FDF2F8]/40">{desc}</p>
                          </div>
                        </div>
                        {formData.features[key] ? (
                          <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* White-Label Branding Control */}
              <div className="bg-[#4A0A66]/20 rounded-lg p-4 border border-[#FF7901]/50">
                <h3 className="text-sm font-medium text-[#FF7901] mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  White-Label Branding Control
                </h3>
                <p className="text-xs text-[#FDF2F8]/50 mb-3">Turn these OFF for a fully white-labeled experience</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: 'show_powered_by', label: '"Powered by UnCloned"', desc: 'Shows in client portal footer' },
                    { key: 'show_upgrade_prompts', label: 'Upgrade Prompts', desc: 'Hide upgrade banners for users' }
                  ].map(({ key, label, desc }) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.features[key] 
                          ? 'bg-[#4A0A66]/20 border-[#4A0A66]' 
                          : 'bg-[#ef4444]/10 border-[#ef4444]/50'
                      }`}
                      onClick={() => setFormData({
                        ...formData,
                        features: { ...formData.features, [key]: !formData.features[key] }
                      })}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm text-white">{label}</span>
                          {desc && <p className="text-xs text-[#FDF2F8]/40">{desc}</p>}
                        </div>
                        {formData.features[key] ? (
                          <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#ef4444]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <SectionSaveButton sectionName="Features" />
          </div>

          {/* White-Label Customization */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FF7901]" />
              White-Label Customization
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">App Name</label>
                <Input
                  value={formData.app_name}
                  onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                  placeholder="Journal"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">AI Coach Name</label>
                <Input
                  value={formData.ai_coach_name}
                  onChange={(e) => setFormData({ ...formData, ai_coach_name: e.target.value })}
                  placeholder="Your Journal Coach"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Custom Domain</label>
                <Input
                  value={formData.custom_domain}
                  onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                  placeholder="journal.clientcompany.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Support Email</label>
                <Input
                  value={formData.support_email}
                  onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                  placeholder="support@clientcompany.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>

            {/* DNS Guide */}
            {formData.custom_domain && (
              <div className="bg-[#4A0A66]/20 rounded-lg p-4 border border-[#4A0A66]/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-[#0FFFE2] flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    DNS Configuration Instructions
                  </h3>
                  <Button
                    size="sm"
                    type="button"
                    variant="outline"
                    className="text-xs border-[#4A0A66] text-[#FDF2F8]/70"
                    onClick={() => {
                      const instructions = `DNS Configuration for ${formData.custom_domain}\n\nRecord Type: CNAME\nHost: ${formData.custom_domain.split('.')[0] || 'journal'}\nValue: app.unclonedjournal.com\nTTL: 3600`;
                      navigator.clipboard.writeText(instructions);
                      toast.success('DNS instructions copied!');
                    }}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="bg-[#1a0527] rounded-lg p-3 font-mono text-xs">
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-[#0FFFE2]">Type:</span>
                    <span className="text-white">CNAME</span>
                    <span className="text-[#0FFFE2]">Host:</span>
                    <span className="text-white">{formData.custom_domain.split('.')[0] || 'journal'}</span>
                    <span className="text-[#0FFFE2]">Value:</span>
                    <span className="text-[#FF7901]">app.unclonedjournal.com</span>
                  </div>
                </div>
              </div>
            )}
            <SectionSaveButton sectionName="White-Label" />
          </div>

          {/* Footer & Legal */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#0FFFE2]" />
              Footer & Legal Links
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Company Name (footer)</label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Client Company Inc."
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Copyright Text</label>
                <Input
                  value={formData.copyright_text}
                  onChange={(e) => setFormData({ ...formData, copyright_text: e.target.value })}
                  placeholder="© 2026 Client Company. All rights reserved."
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Privacy Policy URL</label>
                <Input
                  value={formData.privacy_policy_url}
                  onChange={(e) => setFormData({ ...formData, privacy_policy_url: e.target.value })}
                  placeholder="https://clientcompany.com/privacy"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Terms of Service URL</label>
                <Input
                  value={formData.terms_url}
                  onChange={(e) => setFormData({ ...formData, terms_url: e.target.value })}
                  placeholder="https://clientcompany.com/terms"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>
            <SectionSaveButton sectionName="Footer & Legal" />
          </div>

          {/* Theme Colors - EXPANDED 8-COLOR SYSTEM */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-[#df1283]" />
              Brand Colors
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              These 8 colors give full control over the client's portal appearance and depth
            </p>
            
            {/* Core Colors Row */}
            <h3 className="text-sm font-medium text-[#FDF2F8]/80 mb-3">Core Colors</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { key: 'brand_color', label: 'Brand Color', hint: 'Buttons, links' },
                { key: 'background_color', label: 'Background', hint: 'Page background' },
                { key: 'card_color', label: 'Card Color', hint: 'Panels, sections' },
                { key: 'text_color', label: 'Text Color', hint: 'Primary text' }
              ].map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="block text-sm text-[#FDF2F8]/70 mb-1">{label}</label>
                  <span className="block text-xs text-[#FDF2F8]/40 mb-2">{hint}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="w-10 h-10 rounded-full cursor-pointer bg-transparent border-2 border-[#4A0A66] appearance-none [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
                    />
                    <Input
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-xs flex-1"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Colors Row for Depth */}
            <h3 className="text-sm font-medium text-[#FDF2F8]/80 mb-3">Additional Colors (Depth & Contrast)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'border_color', label: 'Border Color', hint: 'Card borders, dividers' },
                { key: 'accent_color', label: 'Accent Color', hint: 'Highlights, badges' },
                { key: 'secondary_text_color', label: 'Secondary Text', hint: 'Muted, helper text' },
                { key: 'input_background', label: 'Input Background', hint: 'Form fields' }
              ].map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="block text-sm text-[#FDF2F8]/70 mb-1">{label}</label>
                  <span className="block text-xs text-[#FDF2F8]/40 mb-2">{hint}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="w-10 h-10 rounded-full cursor-pointer bg-transparent border-2 border-[#4A0A66] appearance-none [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
                    />
                    <Input
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-xs flex-1"
                    />
                  </div>
                </div>
              ))}
            </div>
            <SectionSaveButton sectionName="Brand Colors" />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90 px-8"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      
      <Footer />
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl p-6 max-w-md w-full border border-red-500/30 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Delete Client?</h2>
              <p className="text-[#FDF2F8]/60">
                This will permanently delete <strong className="text-white">{formData.name}</strong> and all their data. This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className="flex-1 border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteClient}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Password Reset Modal */}
      {showPasswordModal && newCredentials && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowPasswordModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-[#22c55e]" />
                New Password Generated
              </h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <X className="w-5 h-5 text-[#FDF2F8]/70" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg">
                <p className="text-[#22c55e] text-sm mb-3">
                  Password reset successful! Share these credentials with your client:
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[#FDF2F8]/70 text-sm">Portal URL:</span>
                    <code className="text-white text-sm bg-[#4A0A66]/50 px-2 py-1 rounded">
                      /client-portal
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#FDF2F8]/70 text-sm">Client ID:</span>
                    <code className="text-white text-sm bg-[#4A0A66]/50 px-2 py-1 rounded">
                      {newCredentials.admin_username}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#FDF2F8]/70 text-sm">New Password:</span>
                    <code className="text-[#FF7901] text-sm bg-[#4A0A66]/50 px-2 py-1 rounded font-bold">
                      {newCredentials.new_password}
                    </code>
                  </div>
                </div>
              </div>
              
              <p className="text-[#FDF2F8]/50 text-xs">
                This password will only be shown once. Make sure to copy it now.
              </p>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={copyCredentials}
                  className="flex-1 bg-gradient-to-r from-[#df1283] to-[#FF7901]"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Credentials
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordModal(false)}
                  className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditClientPage;
